import React, { useState, useEffect } from "react";
import {
  FaSearch,
  FaChevronDown,
  FaChevronUp,
  FaInfoCircle,
} from "react-icons/fa";

import useDebounce from "../../../../hooks/useDebounce";
import accessURL from "../../../../components/utils/access";
import { useDateOfVisit } from "../../../../contexts/report/DateOfVisitContext";

export interface CustomerSelectorProps {
  selectedCustomer: Customer | null;
  onCustomerSelect: (customer: Customer | null) => void;
  error?: string;
}

interface Customer {
  customer: string;
  time_of_visit: string;
}

interface PaginationInfo {
  currentPage: number;
  totalPages: number;
  totalCustomers: number;
  limit: number;
}

interface CustomerDetailsModalProps {
  customer: Customer;
  onClose: () => void;
}

const CustomerSelector = ({
  onCustomerSelect,
  selectedCustomer,
  error,
}: CustomerSelectorProps) => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);

  const [pagination, setPagination] = useState<PaginationInfo>({
    currentPage: 1,
    totalPages: 1,
    totalCustomers: 0,
    limit: 10,
  });

  const { dateOfVisit } = useDateOfVisit() ?? {};
  const debouncedSearch = useDebounce(searchTerm, 300);

  const fetchCustomers = async (pageNum: number, isNewSearch = false) => {
    try {
      setLoading(true);
      const response = await accessURL.get(
        `/reports/customers?page=${pageNum}&limit=${pagination.limit
        }&dateOfVisit=${dateOfVisit}${searchTerm ? `&name=${searchTerm}` : ""}`
      );

      // Handle case when response has null data and pagination
      if (response.data.data === null && response.data.pagination === null) {
        if (isNewSearch) {
          setCustomers([]);
          onCustomerSelect(null);
        }
        setPagination({
          currentPage: 1,
          totalPages: 1,
          totalCustomers: 0,
          limit: 10,
        });
        return;
      }

      const newCustomers = response.data?.data || [];

      // Clear selected customer if it's not in the new data on a new search
      if (isNewSearch && selectedCustomer) {
        const customerStillExists = newCustomers.some(
          (c: Customer) => c.customer === selectedCustomer.customer
        );
        if (!customerStillExists) {
          onCustomerSelect(null);
        }
      }

      if (isNewSearch) {
        setCustomers(newCustomers);
      } else {
        setCustomers((prev) => [...prev, ...newCustomers]);
      }

      setPagination(response.data.pagination);
    } catch (error) {
      console.error("Error fetching customers:", error);
      // Clear customers and selected customer on error
      setCustomers([]);
      onCustomerSelect(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    fetchCustomers(1, true);
  }, [debouncedSearch, dateOfVisit]);

  const loadMoreCustomers = () => {
    if (pagination.currentPage < pagination.totalPages) {
      fetchCustomers(pagination.currentPage + 1);
    }
  };

  return (
    <div className="w-full">
      <label className="block text-sm font-medium text-gray-700 mb-1">
        Select Customer
      </label>

      {selectedCustomer && (
        <div className="mb-2">
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm bg-blue-100 text-blue-800">
            {selectedCustomer.customer}{" "}
            <button
              type="button"
              className="ml-1 text-blue-600 hover:text-blue-800"
              onClick={() => onCustomerSelect(null)}
            >
              ×
            </button>
          </span>
        </div>
      )}

      <div className="relative">
        <button
          type="button"
          onClick={() => {
            setIsOpen(!isOpen);
            if (!isOpen && onCustomerSelect) {
              onCustomerSelect(selectedCustomer);
            }
          }}
          className="w-full bg-white border border-gray-300 rounded-md py-2 px-3 text-left shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
        >
          <span className="flex items-center justify-between">
            <span className="block truncate">
              {selectedCustomer ? selectedCustomer.customer : "Select customer"}
            </span>
            {isOpen ? (
              <FaChevronUp className="text-gray-400" />
            ) : (
              <FaChevronDown className="text-gray-400" />
            )}
          </span>
        </button>

        {isOpen && (
          <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-96 rounded-md border border-gray-300">
            <div className="sticky top-0 bg-white p-2 border-b border-gray-200">
              <div className="relative">
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md pl-10 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Search customers..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              </div>
            </div>

            <div className="overflow-auto max-h-72">
              {customers.map((customer) => (
                <div
                  key={`${customer.customer}-${Math.random()
                    .toString(36)
                    .substring(7)}`}
                  className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                >
                  <div className="flex justify-between items-center">
                    <div
                      onClick={() => {
                        onCustomerSelect(customer);
                        setIsOpen(false);
                      }}
                      className="flex-grow"
                    >
                      <div className="font-medium">{customer.customer}</div>
                      <div className="text-sm text-gray-500">
                        {customer.time_of_visit}
                      </div>
                    </div>
                    <button className="text-blue-600 hover:text-blue-800 p-2">
                      <FaInfoCircle />
                    </button>
                  </div>
                </div>
              ))}

              {!loading && pagination.currentPage < pagination.totalPages && (
                <button
                  type="button"
                  onClick={loadMoreCustomers}
                  className="w-full py-2 text-sm text-blue-600 hover:text-blue-800 hover:bg-gray-50 border-t"
                >
                  Load more ({pagination.totalCustomers - customers.length}{" "}
                  remaining)
                </button>
              )}

              {loading && (
                <div className="p-4 text-center text-gray-500">Loading...</div>
              )}
              {!loading && customers.length === 0 && (
                <div className="p-4 text-center text-gray-500">
                  No customers found
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );
};

export default CustomerSelector;
